import React from 'react';

import { Alert, Chip, Paper, Stack, Typography } from '@mui/material';

import { useGetUserPromoRoles } from '~/hooks/apis/promo/useGetUserPromoRoles';

import { PromoRole } from '~/services/PromoService';

type CurrentUserRoleSummaryProps = {
  userId: string;
  authRoles: Array<string>;
  availableRoles: Array<PromoRole>;
};

export default function CurrentUserRoleSummary({ userId, authRoles, availableRoles }: CurrentUserRoleSummaryProps) {
  const { data: userPromoRoles, isPending, error } = useGetUserPromoRoles(userId);
  const sortedAuthRoles = [...authRoles].sort((a, b) => a.localeCompare(b));
  const missingRoles = availableRoles
    .filter((role) => role.isActive && !userPromoRoles?.some((userRole) => userRole.roleId === role.roleId))
    .sort((a, b) => a.roleName.localeCompare(b.roleName));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Your roles</Typography>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Auth roles</Typography>
          <Typography variant="caption" color="text.secondary">
            Promo-related auth roles are highlighted.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {sortedAuthRoles.map((role) => (
              <Chip
                key={role}
                label={role}
                size="small"
                color={role.toLowerCase().includes('promo') ? 'primary' : 'default'}
              />
            ))}
            {!sortedAuthRoles.length && <Typography variant="body2">No auth roles assigned</Typography>}
          </Stack>
        </Stack>
        {isPending ? (
          <Typography variant="body2" color="text.secondary">
            Loading your promo roles…
          </Typography>
        ) : error ? (
          <Alert severity="error">Could not load your promo roles: {error.message}</Alert>
        ) : (
          <>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Your promo roles</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {[...(userPromoRoles ?? [])]
                  .sort((a, b) => a.roleName.localeCompare(b.roleName))
                  .map((role) => (
                    <Chip
                      key={role.roleId}
                      label={role.isActive ? role.roleName : `${role.roleName} (inactive)`}
                      size="small"
                      variant={role.isActive ? 'filled' : 'outlined'}
                    />
                  ))}
                {!userPromoRoles?.length && <Typography variant="body2">No promo roles assigned</Typography>}
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Active promo roles you don’t have</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {missingRoles.map((role) => (
                  <Chip key={role.roleId} label={role.roleName} size="small" color="warning" variant="outlined" />
                ))}
                {!missingRoles.length && (
                  <Typography variant="body2">You have all active promo roles listed here</Typography>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}
